/**
 * board — `SlotBoard`, the POSITIONED-SLOT plate (KG-25).
 *
 * The one composition the kit could not express at all. `Grid` divides a box
 * into equal cells and `Overlay` layers children over a base, but neither can
 * PLACE a child: a flat-lay plate is N boxes at authored `%` coordinates, each
 * of which holds the row whose category it is for. Prod draws three of them
 * (the builder screen, the collection tile, the detail hero) off one table of
 * `{cat,x,y,w,h}` rectangles, and every one of them was lost in the port.
 *
 * NO domain vocabulary: this places boxes and fills them from a bound list by
 * matching one FIELD. What the boxes are for is the blueprint's business — the
 * `cat` of a slot is just the value the matching field must equal.
 *
 * TWO variants, because prod uses the same table two ways:
 *   `board`  the tappable builder — an unfilled slot draws a `+` invitation,
 *            and every slot emits `slotActivated` so a page can open a picker;
 *   `flat`   the read-only plate — unfilled slots draw NOTHING and no slot
 *            takes a tap, which is what makes it droppable inside a card.
 *
 * SEALED: a slot is a percentage box inside a fixed-aspect plate, so the whole
 * arrangement scales with the width and can never overflow it; and one row
 * fills at most one slot (`used`), so a list with two rows of the same category
 * fills two boxes rather than painting the first one twice.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { num } from '../paths';
import { ratioValue, resetButton } from './ui';

/** The dotted plate prod draws behind the builder — a token grid, not a hex. */
const DOT_PLATE = {
  backgroundImage: 'radial-gradient(var(--line) 1px, transparent 1.2px)',
  backgroundSize: '15px 15px',
};

const norm = (v) => String(v ?? '').trim().toLowerCase();

/** A row's identity, in the kit's usual order of preference. */
const rowId = (row) => (row?.item_id ?? row?.id ?? null);

/**
 * `[{cat,x,y,w,h,label?}]` → the boxes this board draws, coordinates clamped.
 *
 * Clamping is the seal: a slot authored at `x:80,w:40` would paint past the
 * plate's edge, and a board that leaks is worse than one that crops — the
 * plate IS the frame the arrangement is composed against.
 */
function slotSpecs(raw) {
  const list = Array.isArray(raw) ? raw : [];
  const out = [];
  for (const s of list) {
    if (!s || typeof s !== 'object') continue;
    const x = Math.max(0, Math.min(100, num(s.x)));
    const y = Math.max(0, Math.min(100, num(s.y)));
    out.push({
      cat: String(s.cat ?? ''),
      label: s.label == null || s.label === '' ? String(s.cat ?? '') : String(s.label),
      x,
      y,
      w: Math.max(1, Math.min(100 - x, num(s.w) || 20)),
      h: Math.max(1, Math.min(100 - y, num(s.h) || 20)),
    });
  }
  return out;
}

/**
 * WHICH ROW SITS IN EACH SLOT.
 *
 * A pushed selection wins (that is the user's own choice, delivered through the
 * `select` slot); otherwise, with `fill: "auto"`, the slot takes the first row
 * it has not already used whose `field` matches its `cat`. `fill: "manual"`
 * leaves every unchosen slot empty, which is what a BUILDER needs: auto-filling
 * a builder from the whole library would hand the user a look they never picked.
 */
function fillSlots(specs, rows, { field, picked, auto }) {
  const used = new Set();
  return specs.map((sl) => {
    if (Object.prototype.hasOwnProperty.call(picked, sl.cat)) {
      const want = picked[sl.cat];
      const row = want == null ? null : rows.find((r) => rowId(r) === want) || null;
      if (row) used.add(row);
      return row;
    }
    if (!auto) return null;
    const row = rows.find((r) => !used.has(r) && norm(r?.[field]) === norm(sl.cat)) || null;
    if (row) used.add(row);
    return row;
  });
}

/** The default cell: the row's picture, contained and lifted off the plate. */
function Cutting({ src, alt }) {
  if (!src) return null;
  return (
    <img
      src={String(src)}
      alt={alt || ''}
      style={{
        maxWidth: '94%', maxHeight: '94%', objectFit: 'contain',
        filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.16))',
      }}
    />
  );
}

/** The `+` invitation an unfilled slot wears in the `board` variant. */
function SlotInvite({ label }) {
  return (
    <>
      <span aria-hidden style={{
        position: 'absolute', inset: 0, borderRadius: 'var(--r-sm)',
        background: 'var(--surface-3)', opacity: 0.55,
      }} />
      <span style={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 6, pointerEvents: 'none',
      }}>
        <span aria-hidden style={{
          width: 30, height: 30, borderRadius: 'var(--r-pill)',
          background: 'var(--text)', color: 'var(--bg)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 'var(--t-lg)', fontWeight: 700, lineHeight: 1,
        }}>+</span>
        {label ? (
          <span style={{ fontSize: 'var(--t-xs)', fontWeight: 600, color: 'var(--text-3)' }}>{label}</span>
        ) : null}
      </span>
    </>
  );
}

/**
 * SlotBoard — N `%`-placed slots on one fixed-aspect plate.
 *
 * Props: `slots` (`[{cat,x,y,w,h,label?}]`, a literal table or a bind) ·
 * `fill` (`auto` | `manual`) · `match` (the row field a slot's `cat` is
 * compared against, default `category`) · `imageField` (default `image_url`) ·
 * `cell` (a NODE template rendered per filled slot, which is how a slot holds
 * something richer than a picture) · `aspect` · `texture: "dots"` ·
 * `variant` (`board` | `flat`).
 *
 * Signals: `slotActivated {cat,index,item_id,filled}` — one wire opens a picker
 * for the category that was tapped, filled or not (prod re-opens a filled slot
 * to change it); `selectionChanged {cat,item_id,item_ids,count}` after every
 * `select` delivery, so a page can park the picked ids in `page_args` and a
 * submit button's payload map can read them. That is the whole reason the
 * signal carries `item_ids` in SLOT ORDER: the order is the arrangement's, and
 * only the board knows it.
 *
 * Slot: `select {cat,item_id}` — the picker's answer. A `null`/absent `item_id`
 * CLEARS that slot, so one wire both fills and empties.
 */
export function SlotBoard({
  nodeId, variant, props = {}, data, emit, slots: inbox, renderTemplate,
}) {
  const flat = (props.variant || variant) === 'flat';
  const specs = useMemo(() => slotSpecs(props.slots), [props.slots]);
  const rows = useMemo(() => {
    if (Array.isArray(data)) return data.filter((r) => r && typeof r === 'object');
    return data && typeof data === 'object' ? [data] : [];
  }, [data]);

  const [picked, setPicked] = useState({});
  const emitRef = useRef(emit);
  useEffect(() => { emitRef.current = emit; }, [emit]);

  // ONE delivery per `seq`: pushing the same piece into the same slot twice is
  // two answers from the picker, and the second must not be swallowed.
  const pushed = inbox?.select;
  const seen = useRef(null);
  useEffect(() => {
    if (!pushed || pushed.seq === seen.current) return;
    seen.current = pushed.seq;
    const payload = pushed.payload || {};
    const cat = String(payload.cat ?? payload.key ?? '');
    if (!cat) return;
    const id = payload.item_id ?? payload.id ?? null;
    const next = { ...picked, [cat]: id };
    setPicked(next);
    const ids = specs
      .map((sl) => (Object.prototype.hasOwnProperty.call(next, sl.cat) ? next[sl.cat] : null))
      .filter((v) => v != null);
    emitRef.current?.('selectionChanged', {
      cat, item_id: id, item_ids: ids, count: ids.length,
    });
  }, [pushed, picked, specs]);

  if (!specs.length) return null;

  const field = props.match || 'category';
  const imageField = props.imageField || 'image_url';
  // The interpreter's `renderTemplate`, or the seam a surface with no
  // interpreter around it hands in — the same arrangement collections use.
  const layOut = renderTemplate || props.renderTemplate;
  const filled = fillSlots(specs, rows, {
    field, picked, auto: (props.fill || 'auto') !== 'manual',
  });

  const plate = {
    position: 'relative', width: '100%', minWidth: 0,
    aspectRatio: ratioValue(props.aspect, '1 / 1.2'),
    borderRadius: 'var(--r-lg)', overflow: 'hidden',
    background: 'var(--surface)', boxShadow: 'var(--shadow-1)',
    ...(props.texture === 'dots' ? DOT_PLATE : null),
  };

  return (
    <div style={plate}>
      {specs.map((sl, i) => {
        const row = filled[i];
        if (flat && !row) return null;

        const body = row
          ? (props.cell && layOut
            ? layOut(props.cell, {
              item: row,
              key: `${nodeId}__cell${i}`,
              onSignal: (signal, payload) => emit?.(signal, { index: i, cat: sl.cat, ...(payload || {}) }),
            })
            : <Cutting src={row[imageField]} alt={row.name} />)
          : <SlotInvite label={sl.label} />;

        const box = {
          position: 'absolute',
          left: `${sl.x}%`, top: `${sl.y}%`, width: `${sl.w}%`, height: `${sl.h}%`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minWidth: 0,
        };

        if (flat) return <div key={`${sl.cat}-${i}`} style={box}>{body}</div>;

        return (
          <button
            key={`${sl.cat}-${i}`}
            type="button"
            aria-label={sl.label || sl.cat}
            data-vi-slot={sl.cat}
            onClick={() => emit?.('slotActivated', {
              cat: sl.cat, index: i, item_id: rowId(row), filled: Boolean(row),
            })}
            // A slot is exactly as big as the arrangement says. The kit's 44pt
            // hit floor is deliberately NOT applied here: growing a box to
            // reach it would move it off the coordinates the plate is composed
            // from, and the smallest slot prod authors is already 19% of a
            // full-width plate — comfortably past the floor on every phone.
            style={{ ...resetButton, ...box }}
          >{body}</button>
        );
      })}
    </div>
  );
}
